import React, { useState } from "react";
import * as Yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { IconButton, InputAdornment } from "@mui/material";
import styled from "@emotion/styled";
import EyeOffIcon from "@icons/myicons/eye-cross.svg?react";
import EyeIcon from "@icons/myicons/eye.svg?react";
import { LoadingButton, RHFCheckbox, RHFTextField } from "@components";
import { useLogin, useRememberMe } from "@api/v1/hooks";
import { TwoFAFormData } from "../types";
import { LoginErrorBanner } from "./LoginErrorBanner";

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const LoadingButtonStyled = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

interface LoginFormFields {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  setTwoFAFormData: ({
    secret,
    email,
    password,
    rememberMe,
  }: TwoFAFormData) => void;
  onLogin: (access_token: string) => void;
}

export const LoginPageForm = ({ setTwoFAFormData, onLogin }: Props) => {
  const { t } = useTranslation();
  const [storedRememberMe] = useRememberMe();

  const [showPassword, setShowPassword] = useState(false);

  const loginFormSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("form.errors.wrongEmailFormat"))
      .required(t("form.errors.required")),
    password: Yup.string().required(t("form.errors.required")),
    rememberMe: Yup.boolean(),
  });

  const { handleSubmit, control } = useForm<LoginFormFields>({
    resolver: yupResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: storedRememberMe ?? false,
    },
  });

  const { loginAsync, isFetching, tokenError } = useLogin();

  const onSubmit: SubmitHandler<LoginFormFields> = async (fields) => {
    try {
      const result = await loginAsync({
        grant_type: "password",
        username: fields.email,
        password: fields.password,
        rememberMe: fields.rememberMe,
      });
      if (result.twoFactorAuthenticationRequired) {
        if (!result.secret) throw new Error("Secret is required");
        setTwoFAFormData({
          secret: result.secret,
          email: fields.email,
          password: fields.password,
          rememberMe: fields.rememberMe,
        });
        return;
      }
      if (!result.access_token) throw new Error("Access token is required");
      onLogin(result.access_token);
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    }
  };

  const handleClickShowPassword = () =>
    setShowPassword((prevState) => !prevState);

  return (
    <FormStyled onSubmit={handleSubmit(onSubmit)} data-testid="login-form">
      <LoginErrorBanner error={tokenError} />
      <RHFTextField
        fullWidth
        data-testid="user-login-field"
        label={t("loginPage.form.emailField")}
        name="email"
        type="email"
        variant="outlined"
        size="small"
        control={control}
        testId="user-login-field-input"
        placeholder={t("loginPage.form.emailFieldPlaceholder")}
      />
      <RHFTextField
        fullWidth
        data-testid="user-password-field"
        variant="outlined"
        label={t("loginPage.form.passwordField")}
        name="password"
        size="small"
        type={showPassword ? "text" : "password"}
        control={control}
        testId="user-password-field-input"
        placeholder={t("loginPage.form.passwordFieldPlaceholder")}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleClickShowPassword}
                  size="small"
                >
                  {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <RHFCheckbox
        control={control}
        name="rememberMe"
        label={t("loginPage.form.rememberMe")}
      />
      <LoadingButtonStyled
        fullWidth
        type="submit"
        variant="primary"
        data-testid="submit-user-login"
        isFetching={isFetching}
      >
        {t("loginPage.form.submitButton")}
      </LoadingButtonStyled>
    </FormStyled>
  );
};
